// SketchOverlayUI — DOM-оверлеи поверх 3D-канваса.
//   - 'select_plane': подсказка + кнопка отмены
//   - 'sketch':       палитра + размерные input'ы (Fusion 360 style)

import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import useCadStore from '../store/useCadStore';
import RU from '../i18n/ru';
import { getSolverClient } from '../solverClient';
import cl from './SketchCanvas.module.css';
import { computeParameters, resolveValue } from '../parameters';


// === Один input размера в Fusion-стиле ===
//
// onFocus → автоматически lock'ает значение (чтобы мышь не перезаписывала).
// onChange → сразу применяется к pendingShape (через override-bridge).
// Enter → финализация фигуры.
const DimInput = ({ value, onChange, onCommit, locked, onLockToggle, onFocusLock,
                   autoFocus, inputRef: externalRef }) => {
    const localRef = useRef(null);
    const inputRef = externalRef || localRef;
    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [autoFocus, inputRef]);
    return (
        <div className={cl.dimBox}>
            <input
                ref={inputRef}
                /*
                 * 🔴 Текстовое поле, а не number.
                 *
                 * В input[type=number] браузер не даёт ввести буквы и скобки —
                 * формулу набрать невозможно в принципе. А размер на холсте
                 * ровно то место, где хочется написать «толщина * 2»: рисуешь
                 * деталь и сразу привязываешь её к параметру, как в Fusion.
                 */
                type="text"
                inputMode="decimal"
                className={cl.dimInput}
                value={value}
                title="Можно вводить формулу: толщина * 2"
                onFocus={() => onFocusLock?.()}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        onCommit?.();
                    } else if (e.key === 'Escape') {
                        e.preventDefault();
                        e.target.blur();
                    }
                    /* Tab здесь НЕ обрабатываем: он ловится одним обработчиком
                       на уровне окна (DrawDimensions) — во время рисования
                       фокус часто вообще вне полей, и локальный обработчик
                       до клавиши не доходит. */
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
            />
            <span className={cl.dimUnit}>мм</span>
            {onLockToggle && (
                <button
                    className={cl.dimLock}
                    onClick={onLockToggle}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    title={locked ? 'Зафиксировано (не следует за мышью)' : 'Зафиксировать'}
                >
                    {locked ? '🔒' : '🔓'}
                </button>
            )}
        </div>
    );
};


/*
 * Смещение корня оверлея относительно окна.
 *
 * Живёт вне компонента: читать layout (getBoundingClientRect) МОЖНО только
 * после коммита, иначе браузер пересчитывает вёрстку посреди работы React —
 * см. подробный комментарий в DrawDimensions. Обновляется в useEffect и при
 * ресайзе окна.
 */
const overlayOffsetRef = { current: { x: 0, y: 0 } };

function refreshOverlayOffset() {
    const el = document.getElementById('kubikon-cad-overlay-root');
    if (!el) return;
    const r = el.getBoundingClientRect();
    overlayOffsetRef.current = { x: r.left, y: r.top };
}


// === Размерные оверлеи ===
const DrawDimensions = () => {
    const pendingShape = useCadStore((s) => s.pendingShape);
    const drawSizeOverride = useCadStore((s) => s.drawSizeOverride);
    const setDrawSizeOverride = useCadStore((s) => s.setDrawSizeOverride);

    // Локальные значения input'ов
    const [widthInput, setWidthInput] = useState('');
    const [heightInput, setHeightInput] = useState('');
    const [lengthInput, setLengthInput] = useState('');
    const [radiusInput, setRadiusInput] = useState('');

    // Что зафиксировано
    const [lockedW, setLockedW] = useState(false);
    const [lockedH, setLockedH] = useState(false);
    const [lockedL, setLockedL] = useState(false);
    const [lockedR, setLockedR] = useState(false);

    // Ссылки на поля — нужны для Tab-перехода между размерами
    // и для того, чтобы цифры с клавиатуры попадали в нужный input.
    const widthRef = useRef(null);
    const heightRef = useRef(null);
    const lengthRef = useRef(null);
    const radiusRef = useRef(null);

    // Сброс при новом pendingShape (по start)
    const startKey = pendingShape ? `${pendingShape.start.x},${pendingShape.start.y},${pendingShape.tool}` : null;
    const prevStartKey = useRef(null);
    useEffect(() => {
        if (startKey !== prevStartKey.current) {
            prevStartKey.current = startKey;
            setLockedW(false); setLockedH(false); setLockedL(false); setLockedR(false);
            setWidthInput(''); setHeightInput(''); setLengthInput(''); setRadiusInput('');
        }
    }, [startKey]);

    /*
     * ЦИФРЫ С КЛАВИАТУРЫ ПОПАДАЮТ В РАЗМЕР, ГДЕ БЫ НИ БЫЛ ФОКУС.
     *
     * Поля появляются с autoFocus, но стоит повести мышью по канвасу — и
     * фокус уходит на него (канвас ловит pointer-события). После этого
     * набранные цифры проваливались в никуда, и приходилось лишний раз
     * целиться мышью в маленький input.
     *
     * Теперь любая цифра (а также «.», «,» и «-») во время рисования
     * попадает в активный размер: поле фокусируется, фиксируется от мыши
     * и получает символ. Это тот же поток, что в Fusion: ведёшь мышью —
     * набираешь число — Tab/Enter.
     *
     * Перехватываем ТОЛЬКО когда фокус не в поле ввода: если человек уже
     * печатает в input (или в любом другом текстовом поле редактора),
     * вмешиваться нельзя.
     */
    useEffect(() => {
        if (!pendingShape) return undefined;

        const focusField = (ref, lock) => {
            if (!ref?.current) return false;
            lock?.(true);                    // мышь больше не перетирает значение
            ref.current.focus();
            ref.current.select();
            return true;
        };

        const onKeyDown = (e) => {
            if (e.ctrlKey || e.altKey || e.metaKey) return;
            const el = document.activeElement;
            const tag = el?.tagName;
            const inField = tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable;
            const t = pendingShape.tool;

            /*
             * TAB — ПЕРЕХОД ПО РАЗМЕРАМ, ГДЕ БЫ НИ БЫЛ ФОКУС.
             *
             * 🔴 Ловим на уровне ОКНА, а не на самом input.
             *
             * Пока ведёшь фигуру мышью, фокус находится на канвасе (он ловит
             * pointer-события) — то есть НЕ в поле. Обработчик, висящий на
             * input, до Tab просто не доходит, и браузер уводит фокус по
             * своему порядку табуляции: на кнопки нижней панели («рука»,
             * лупы). Попасть табом в размер было физически невозможно.
             *
             * Теперь Tab всегда ведёт по размерам текущей фигуры:
             * у прямоугольника ширина → высота → ширина, у остальных
             * фокус встаёт на их единственное поле.
             */
            if (e.key === 'Tab') {
                let ok = false;
                if (t === 'rectangle') {
                    // Если фокус уже в ширине — идём на высоту, иначе в ширину.
                    ok = (el === widthRef.current)
                        ? focusField(heightRef, setLockedH)
                        : focusField(widthRef, setLockedW);
                } else if (t === 'circle') {
                    ok = focusField(radiusRef, setLockedR);
                } else if (t === 'line' || t === 'constructionLine') {
                    ok = focusField(lengthRef, setLockedL);
                }
                if (ok) e.preventDefault();
                return;
            }

            // Дальше — только ввод цифр, и только когда фокус вне поля.
            if (inField) return;

            const isDigit = e.key.length === 1 && e.key >= '0' && e.key <= '9';
            const isSep = e.key === '.' || e.key === ',' || e.key === '-';
            if (!isDigit && !isSep) return;

            // Куда писать: у прямоугольника — ширина, у остальных — их размер.
            let ref = null, setter = null, lock = null;
            if (t === 'rectangle')      { ref = widthRef;  setter = setWidthInput;  lock = setLockedW; }
            else if (t === 'circle')    { ref = radiusRef; setter = setRadiusInput; lock = setLockedR; }
            else if (t === 'line' || t === 'constructionLine')
                                        { ref = lengthRef; setter = setLengthInput; lock = setLockedL; }
            if (!ref?.current) return;

            e.preventDefault();
            lock(true);
            const ch = e.key === ',' ? '.' : e.key;
            setter(ch);                       // начинаем НОВОЕ число, а не дописываем
            ref.current.focus();
            // Каретку ставим в конец, чтобы следующие цифры дописывались.
            requestAnimationFrame(() => {
                const inp = ref.current;
                if (inp) inp.setSelectionRange(inp.value.length, inp.value.length);
            });
        };

        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [pendingShape]);

    // Обновляем input'ы по pendingShape (если не locked).
    //
    // Для прямоугольника считаем width/height по ЭКРАННЫМ размерам:
    //   width  = расстояние по ГОРИЗОНТАЛИ экрана между start и end
    //   height = по ВЕРТИКАЛИ экрана.
    // Это важно для повёрнутых плоскостей (XZ/YZ), где локальная X
    // эскиза может проецироваться в вертикаль экрана и наоборот.
    useEffect(() => {
        if (!pendingShape) return;
        const dxLocal = pendingShape.end.x - pendingShape.start.x;
        const dyLocal = pendingShape.end.y - pendingShape.start.y;

        // Вспомогательная линия — та же линия: у неё тоже есть длина,
        // и вводить её точно так же нужно (отступ «20 мм от края»).
        if (pendingShape.tool === 'line' || pendingShape.tool === 'constructionLine') {
            if (!lockedL) setLengthInput(String(Math.sqrt(dxLocal*dxLocal + dyLocal*dyLocal).toFixed(0)));
            return;
        }
        if (pendingShape.tool === 'circle') {
            /*
             * Показываем ДИАМЕТР, а не радиус.
             *
             * На чертежах круглое всегда задаётся через Ø: отверстия, валы,
             * шайбы. В заданиях курса тоже «Ø20 мм», «Ø4 мм под кольцо» —
             * а поле просило радиус, и приходилось делить в уме. Это уже
             * приводило к путанице при прохождении уроков.
             *
             * Внутри окружность по-прежнему хранит радиус: пересчёт делаем
             * только на границе с пользователем (здесь и в override ниже).
             */
            if (!lockedR) {
                const d = 2 * Math.sqrt(dxLocal * dxLocal + dyLocal * dyLocal);
                setRadiusInput(String(d.toFixed(0)));
            }
            return;
        }
        if (pendingShape.tool === 'rectangle') {
            // Считаем размеры через screen-проекцию start/end.
            const project = window.__kubikonProjectToScreen;
            if (typeof project === 'function') {
                const s = project(pendingShape.start.x, pendingShape.start.y);
                const e = project(pendingShape.end.x,   pendingShape.end.y);
                if (s && e) {
                    // Чтобы значение в мм совпадало с локальной мм-метрикой,
                    // считаем размер по локальной оси, выбранной как
                    // «горизонтальная» / «вертикальная» на экране.
                    const sX = project(pendingShape.start.x + 1, pendingShape.start.y);
                    const sY = project(pendingShape.start.x, pendingShape.start.y + 1);
                    if (sX && sY) {
                        const dXh = sX.x - s.x, dYh = sY.x - s.x;
                        const xIsHorizontal = Math.abs(dXh) >= Math.abs(dYh);
                        const hLocal = xIsHorizontal ? dxLocal : dyLocal;
                        const vLocal = xIsHorizontal ? dyLocal : dxLocal;
                        if (!lockedW) setWidthInput(String(Math.abs(hLocal).toFixed(0)));
                        if (!lockedH) setHeightInput(String(Math.abs(vLocal).toFixed(0)));
                        return;
                    }
                }
            }
            // Фолбэк
            if (!lockedW) setWidthInput(String(Math.abs(dxLocal).toFixed(0)));
            if (!lockedH) setHeightInput(String(Math.abs(dyLocal).toFixed(0)));
        }
    }, [pendingShape, lockedW, lockedH, lockedL, lockedR]);

    // Override применяется когда в input есть валидное число И input заблокирован.
    // Замочек (lockedX) — «не перезаписывать input координатами мыши».
    // Включается автоматически при focus или вручную.
    //
    // ВАЖНО: этот useEffect НЕ должен зависеть от pendingShape, иначе:
    //   input change → applyOverride меняет pendingShape.end → useEffect
    //   снова срабатывает → снова applyOverride → бесконечный цикл.
    // pendingShape проверяем через ref.
    const pendingShapeRef = useRef(null);
    pendingShapeRef.current = pendingShape;

    // Запоминаем последний применённый override, чтобы не вызывать applyOverride
    // повторно с теми же значениями (защита от инфинит-цикла).
    const lastAppliedOverrideRef = useRef('');

    useEffect(() => {
        if (!pendingShapeRef.current) {
            if (drawSizeOverride) setDrawSizeOverride(null);
            lastAppliedOverrideRef.current = '';
            return;
        }
        const o = {};
        /*
         * Значение поля — ВЫРАЖЕНИЕ, а не просто число: «40», «толщина * 2»,
         * «sqrt(16) + 1». Считаем через тот же движок, что и параметры
         * документа, чтобы поведение было одинаковым везде.
         *
         * Некорректный ввод молча игнорируем: человек ещё набирает, и
         * подставлять на каждую букву незаконченную формулу нельзя.
         */
        const paramValues = computeParameters(
            useCadStore.getState().doc.parameters || [],
        ).values;
        const num = (str) => {
            const t = String(str ?? '').trim();
            if (t === '') return null;
            let v;
            try { v = resolveValue(t, paramValues); } catch { return null; }
            return (typeof v === 'number' && isFinite(v) && v >= 0) ? v : null;
        };
        const w = num(widthInput);  if (lockedW && w !== null) o.width  = w;
        const h = num(heightInput); if (lockedH && h !== null) o.height = h;
        const l = num(lengthInput); if (lockedL && l !== null) o.length = l;
        // В поле человек вводит ДИАМЕТР — движку нужен радиус.
        const d = num(radiusInput); if (lockedR && d !== null) o.radius = d / 2;
        const has = Object.keys(o).length > 0;
        const sig = JSON.stringify(o);
        if (sig === lastAppliedOverrideRef.current) return; // ничего не изменилось — выходим

        lastAppliedOverrideRef.current = sig;
        setDrawSizeOverride(has ? o : null);
        if (has && typeof window.__kubikonApplyOverride === 'function') {
            window.__kubikonApplyOverride(o);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lockedW, lockedH, lockedL, lockedR, widthInput, heightInput, lengthInput, radiusInput]);

    if (!pendingShape) return null;
    if (pendingShape.tool === 'point') return null;

    // Конвертация координат эскиза → screen
    const project = window.__kubikonProjectToScreen;
    if (typeof project !== 'function') return null;

    const sScreen = project(pendingShape.start.x, pendingShape.start.y);
    const eScreen = project(pendingShape.end.x,   pendingShape.end.y);
    if (!sScreen || !eScreen) return null;

    // Обёртка для отрисовки HTML-стрелки на канвас
    // Поскольку canvas заполняет ВЕСЬ viewport pane, стрелка как DOM просто
    // лежит поверх. Используем абсолютные screen-координаты. Поскольку наш
    // overlay — child viewportPane, нужны координаты относительно него.
    // Делаем через document — ловим offset родителя.
    /*
     * ⚠️ Смещение оверлея берём из КЭША, а не через getBoundingClientRect().
     *
     * Раньше здесь прямо во время рендера вызывались getElementById +
     * getBoundingClientRect. Чтение геометрии заставляет браузер синхронно
     * пересчитать вёрстку — а происходило это ПОСРЕДИ работы React, когда он
     * вставлял узлы размерных линий. Дерево и реальный DOM расходились, и
     * падало «insertBefore: узел не является дочерним», следом терялся
     * WebGL-контекст. Именно поэтому отключение DrawDimensions
     * (localStorage.cadOff='dims') убирало падение целиком.
     *
     * Позицию оверлея обновляет useEffect ПОСЛЕ коммита (см. ниже) — там
     * читать layout безопасно. Оверлей растянут на весь viewport и почти не
     * двигается, так что кэш всегда актуален.
     */
    const offX = overlayOffsetRef.current.x;
    const offY = overlayOffsetRef.current.y;

    const sx = sScreen.x - offX, sy = sScreen.y - offY;
    const ex = eScreen.x - offX, ey = eScreen.y - offY;

    const commitNow = () => {
        if (typeof window.__kubikonCommitDraw === 'function') {
            window.__kubikonCommitDraw();
        }
    };

    if (pendingShape.tool === 'rectangle') {
        // Координаты сторон прямоугольника
        const minX = Math.min(sx, ex), maxX = Math.max(sx, ex);
        const minY = Math.min(sy, ey), maxY = Math.max(sy, ey);
        const midX = (sx + ex) / 2;
        const midY = (sy + ey) / 2;

        // Ширина — над верхней стороной
        // Высота — слева от левой стороны
        return (
            <div key="dim-rect" style={WRAP}>
                {/* Вся графика размеров — в ОДНОМ svg (см. комментарий у DimSvg) */}
                <DimSvg>
                    {/* Размерная стрелка ширины (выше прямоугольника) */}
                    <DimensionLine
                        x1={minX} y1={minY - 30}
                        x2={maxX} y2={minY - 30}
                    />
                    {/* Подводящие линии вверх */}
                    <DimensionTick x={minX} y1={minY} y2={minY - 30}/>
                    <DimensionTick x={maxX} y1={minY} y2={minY - 30}/>
                    {/* Размерная стрелка высоты (слева, вертикальная) */}
                    <DimensionLine
                        x1={minX - 30} y1={minY}
                        x2={minX - 30} y2={maxY}
                    />
                    <DimensionTick y={minY} x1={minX} x2={minX - 30} horizontal/>
                    <DimensionTick y={maxY} x1={minX} x2={minX - 30} horizontal/>
                </DimSvg>
                {/* Input ширины */}
                <div className={cl.dimOverlay} style={{ left: midX - 50, top: minY - 50 }}>
                    <DimInput
                        value={widthInput}
                        onChange={setWidthInput}
                        onCommit={commitNow}
                        locked={lockedW}
                        onLockToggle={() => setLockedW((v) => !v)}
                        onFocusLock={() => setLockedW(true)}
                        autoFocus
                        inputRef={widthRef}
                        /* Tab обрабатывается ОДНИМ обработчиком на уровне окна
                           (см. «TAB — ПЕРЕХОД ПО РАЗМЕРАМ» выше): фокус во
                           время рисования бывает и вне полей, а два
                           обработчика на одну клавишу дали бы двойной переход. */
                    />
                </div>

                {/* Input высоты */}
                <div className={cl.dimOverlay} style={{ left: minX - 130, top: midY - 12 }}>
                    <DimInput
                        value={heightInput}
                        onChange={setHeightInput}
                        onCommit={commitNow}
                        locked={lockedH}
                        onLockToggle={() => setLockedH((v) => !v)}
                        onFocusLock={() => setLockedH(true)}
                        inputRef={heightRef}
                    />
                </div>

                {/* Кнопка ✓ — финализировать */}
                <button
                    className={cl.commitBtn}
                    style={{ left: maxX + 10, top: minY - 12 }}
                    onClick={commitNow}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    title="Применить размер (Enter)"
                >✓</button>
            </div>
        );
    }

    if (pendingShape.tool === 'line' || pendingShape.tool === 'constructionLine') {
        // Размерная стрелка вдоль линии (с офсетом 20 px перпендикулярно)
        const dx = ex - sx, dy = ey - sy;
        const len = Math.sqrt(dx*dx + dy*dy) || 1;
        // Перпендикулярный единичный вектор (поворот на 90° против часовой)
        const nx = -dy / len, ny = dx / len;
        const off = 22;
        const lx1 = sx + nx*off, ly1 = sy + ny*off;
        const lx2 = ex + nx*off, ly2 = ey + ny*off;
        const mx = (lx1 + lx2) / 2, my = (ly1 + ly2) / 2;

        return (
            <div key="dim-line" style={WRAP}>
                <DimSvg>
                    <DimensionLine x1={lx1} y1={ly1} x2={lx2} y2={ly2}/>
                    {/* Подводящие линии-засечки */}
                    <DimensionLine x1={sx} y1={sy} x2={lx1} y2={ly1} thin/>
                    <DimensionLine x1={ex} y1={ey} x2={lx2} y2={ly2} thin/>
                </DimSvg>

                <div className={cl.dimOverlay} style={{ left: mx - 50, top: my - 12 }}>
                    <DimInput
                        value={lengthInput}
                        onChange={setLengthInput}
                        onCommit={commitNow}
                        locked={lockedL}
                        onLockToggle={() => setLockedL((v) => !v)}
                        onFocusLock={() => setLockedL(true)}
                        autoFocus
                        inputRef={lengthRef}
                    />
                </div>
                <button
                    className={cl.commitBtn}
                    style={{ left: ex + 12, top: ey - 12 }}
                    onClick={commitNow}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    title="Применить размер (Enter)"
                >✓</button>
            </div>
        );
    }

    if (pendingShape.tool === 'circle') {
        // Стрелка от центра к краю окружности
        return (
            <div key="dim-circle" style={WRAP}>
                <DimSvg>
                    <DimensionLine x1={sx} y1={sy} x2={ex} y2={ey}/>
                </DimSvg>
                <div className={cl.dimOverlay} style={{ left: ex + 12, top: ey - 12 }}>
                    <span className={cl.dimArrow}>Ø</span>
                    <DimInput
                        value={radiusInput}
                        onChange={setRadiusInput}
                        onCommit={commitNow}
                        locked={lockedR}
                        onLockToggle={() => setLockedR((v) => !v)}
                        onFocusLock={() => setLockedR(true)}
                        autoFocus
                        inputRef={radiusRef}
                    />
                </div>
                <button
                    className={cl.commitBtn}
                    style={{ left: ex + 12, top: ey + 22 }}
                    onClick={commitNow}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    title="Применить размер (Enter)"
                >✓</button>
            </div>
        );
    }

    return null;
};


/*
 * === Размерные стрелки ===
 *
 * ⚠️ ПОЧЕМУ ОДИН ОБЩИЙ <svg>, А НЕ ПО ОДНОМУ НА ЛИНИЮ.
 *
 * Раньше каждая линия и засечка были ОТДЕЛЬНЫМ <svg> во всю область, и они
 * стояли вперемешку с <div>-полями ввода. Для прямоугольника это восемь узлов,
 * которые появляются разом в момент начала рисования. React вставлял их по
 * очереди в один контейнер, чередуя SVG и HTML (у них разные пространства имён
 * в DOM) — и падал с «insertBefore: узел не является дочерним». Дальше ошибка
 * рендера роняла всё дерево редактора и терялся WebGL-контекст: снаружи это
 * выглядело как «нажал прямоугольник → редактор перезагрузился».
 *
 * Подтверждено экспериментом: с отключённым DrawDimensions
 * (localStorage.cadOff='dims') падение полностью исчезало.
 *
 * Теперь вся графика размеров живёт ВНУТРИ одного <svg>: линии и засечки — его
 * дети-примитивы, а не самостоятельные контейнеры. Число прямых детей оверлея
 * постоянно, чередования SVG/HTML нет.
 */
const DimSvg = ({ children }) => (
    <svg className={cl.dimSvg} style={{ pointerEvents: 'none' }}>{children}</svg>
);

// Линия со стрелками. Возвращает ГРУППУ svg-примитивов (без своего <svg>).
const DimensionLine = ({ x1, y1, x2, y2, thin }) => (
    <g>
        <line
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="#5a7090" strokeWidth={thin ? 0.6 : 1}
            strokeDasharray={thin ? '3 3' : 'none'}
        />
        {/* Стрелочки-наконечники */}
        {!thin && <ArrowHead x={x1} y={y1} angle={Math.atan2(y2 - y1, x2 - x1) + Math.PI}/>}
        {!thin && <ArrowHead x={x2} y={y2} angle={Math.atan2(y2 - y1, x2 - x1)}/>}
    </g>
);

// Выносная засечка — тоже группа примитивов внутри общего <svg>.
const DimensionTick = ({ x, y, x1, x2, y1, y2, horizontal }) => (
    <g>
        {horizontal
            ? <line x1={x1} y1={y} x2={x2} y2={y} stroke="#5a7090" strokeWidth="0.6" strokeDasharray="3 3"/>
            : <line x1={x} y1={y1} x2={x} y2={y2} stroke="#5a7090" strokeWidth="0.6" strokeDasharray="3 3"/>}
    </g>
);

const ArrowHead = ({ x, y, angle }) => {
    const size = 6;
    const a1 = angle + Math.PI - Math.PI / 6;
    const a2 = angle + Math.PI + Math.PI / 6;
    return (
        <polygon
            points={`${x},${y} ${x + Math.cos(a1)*size},${y + Math.sin(a1)*size} ${x + Math.cos(a2)*size},${y + Math.sin(a2)*size}`}
            fill="#5a7090"
        />
    );
};


// === Главный overlay ===
// === Панель числа сторон многоугольника ===
// Видна когда активен инструмент 'polygon'. Позволяет задать N сторон
// ДО рисования (3..64). Прижата к верху по центру.
/*
 * Панель инструмента «Текст» в эскизе (как в 123D Design).
 *
 * Человек вводит надпись и размер ЗДЕСЬ, а потом кликает по плоскости —
 * буквы встают именно туда, куда он показал. Раньше текст задавался в правой
 * панели свойств и появлялся неизвестно где: невозможно было ни выбрать место,
 * ни увидеть результат до применения.
 */
const TextToolPanel = () => {
    const draft = useCadStore((s) => s.textDraft);
    const setTextDraft = useCadStore((s) => s.setTextDraft);

    const text = draft?.text ?? '';
    const fontSize = draft?.fontSize ?? 10;

    return (
        <div
            style={{
                position: 'absolute', top: 12, left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(28,32,40,0.95)',
                border: '1px solid #3a4252', borderRadius: 8,
                padding: '7px 12px', pointerEvents: 'auto',
                color: '#e8eaf0', font: '13px/1.2 system-ui, sans-serif',
                whiteSpace: 'nowrap',
            }}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <span style={{ color: '#9aa4b2' }}>Текст</span>
            <input
                type="text"
                value={text}
                autoFocus
                placeholder="Например: Максим"
                onChange={(e) => setTextDraft({ text: e.target.value, fontSize })}
                style={{
                    width: 170, background: '#1a1f28', color: '#e8eaf0',
                    border: '1px solid #3a4252', borderRadius: 5,
                    padding: '4px 8px', font: 'inherit',
                }}
            />
            <span style={{ color: '#9aa4b2' }}>размер</span>
            <input
                type="number"
                min="1"
                value={fontSize}
                onChange={(e) => setTextDraft({ text, fontSize: Number(e.target.value) || 10 })}
                style={{
                    width: 56, background: '#1a1f28', color: '#e8eaf0',
                    border: '1px solid #3a4252', borderRadius: 5,
                    padding: '4px 8px', font: 'inherit',
                }}
            />
            <span style={{ color: '#9aa4b2' }}>мм</span>
            <span style={{ color: text.trim() ? '#3ecf8e' : '#8b93a5', marginLeft: 4 }}>
                {text.trim() ? 'кликни по плоскости' : 'введи надпись'}
            </span>
        </div>
    );
};


const PolygonSidesPanel = () => {
    const polygonSides = useCadStore((s) => s.polygonSides);
    const setPolygonSides = useCadStore((s) => s.setPolygonSides);
    const [text, setText] = useState(String(polygonSides));

    // Синхронизируем поле с store (если менялось из другого места).
    useEffect(() => { setText(String(polygonSides)); }, [polygonSides]);

    const commit = (val) => {
        const n = parseInt(val, 10);
        if (!Number.isNaN(n)) setPolygonSides(n);
        else setText(String(polygonSides));
    };

    return (
        <div
            style={{
                position: 'absolute', top: 12, left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(28,32,40,0.95)',
                border: '1px solid #3a4252', borderRadius: 8,
                padding: '7px 12px', pointerEvents: 'auto',
                color: '#e6e9ef', fontSize: 13, fontWeight: 600,
                boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
            }}
        >
            <span>Сторон:</span>
            <button
                onClick={() => setPolygonSides(polygonSides - 1)}
                style={polyBtnStyle}
            >−</button>
            <input
                value={text}
                onChange={(e) => setText(e.target.value.replace(/[^\d]/g, ''))}
                onBlur={(e) => commit(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') commit(e.target.value); }}
                style={{
                    width: 44, textAlign: 'center', background: '#11151c',
                    border: '1px solid #3a4252', borderRadius: 5,
                    color: '#fff', fontSize: 14, fontWeight: 700,
                    padding: '4px 0',
                }}
            />
            <button
                onClick={() => setPolygonSides(polygonSides + 1)}
                style={polyBtnStyle}
            >+</button>
        </div>
    );
};
// Панель расстояния для инструмента «Смещение» (offset). >0 наружу, <0 внутрь.
const OffsetDistancePanel = () => {
    const offsetDistance = useCadStore((s) => s.offsetDistance);
    const setOffsetDistance = useCadStore((s) => s.setOffsetDistance);
    const [text, setText] = useState(String(offsetDistance));
    useEffect(() => { setText(String(offsetDistance)); }, [offsetDistance]);
    const commit = (val) => {
        const n = parseFloat(val);
        if (!Number.isNaN(n) && n !== 0) setOffsetDistance(n);
        else setText(String(offsetDistance));
    };
    return (
        <div style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(28,32,40,0.95)', border: '1px solid #3a4252',
            borderRadius: 8, padding: '7px 12px', pointerEvents: 'auto',
            color: '#e6e9ef', fontSize: 13, fontWeight: 600,
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
        }}>
            <span>Смещение (мм, − внутрь):</span>
            <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onBlur={(e) => commit(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') commit(e.target.value); }}
                style={{
                    width: 56, textAlign: 'center', background: '#11151c',
                    border: '1px solid #3a4252', borderRadius: 5, color: '#fff',
                    fontSize: 14, fontWeight: 700, padding: '4px 0',
                }}
            />
        </div>
    );
};

const polyBtnStyle = {
    width: 24, height: 24, borderRadius: 5, border: '1px solid #3a4252',
    background: '#222833', color: '#e6e9ef', fontSize: 16, fontWeight: 700,
    cursor: 'pointer', lineHeight: 1,
};


// Индикатор определённости эскиза (как в Fusion: определён/недоопределён/конфликт).
// status = { dof, conflicts:[], failed } | null
/*
 * Бейдж статуса солвера.
 *
 * ⚠️ Компонент НИКОГДА не возвращает null — он всегда отдаёт один и тот же
 * <div>, а когда показывать нечего, прячется через visibility.
 *
 * Раньше он возвращал null/элемент в зависимости от статуса и стоял среди
 * других условных соседей в оверлее. При выборе инструмента статус менялся,
 * узел то появлялся, то исчезал, React пересчитывал позиции вставки — и падал
 * с «insertBefore: узел не является дочерним». Ошибка рендера роняла всё
 * дерево редактора, а следом терялся WebGL-контекст: снаружи это выглядело как
 * «нажал прямоугольник → страница перезагрузилась».
 *
 * Постоянный узел эту гонку убирает целиком: меняется только содержимое.
 */
// Обёртка-«невидимка»: держит место в дереве React, но не влияет на вёрстку.
const WRAP = { display: 'contents' };

/*
 * ДИАГНОСТИКА падения «insertBefore» при рисовании (2026-08-10).
 *
 * Семь попыток починить структуру дерева не помогли, поэтому ищем виновника
 * экспериментом: блоки оверлея можно гасить по одному прямо в консоли браузера,
 * без пересборки:
 *     localStorage.setItem('cadOff', 'dims');  location.reload();
 * Значения: dims | badge | panels | all. Пусто/нет ключа — всё включено.
 *
 * Как только падение исчезает — виновник найден. После починки блок удалить.
 */
function isOff(part) {
    try {
        const v = localStorage.getItem('cadOff') || '';
        return v === 'all' || v.split(',').includes(part);
    } catch (e) {
        return false;
    }
}

function SketchStatusBadge({ status }) {
    let color = 'transparent', text = '';
    let visible = false;

    if (status) {
        if (status.failed || (status.conflicts && status.conflicts.length)) {
            color = '#cc3344';
            text = status.conflicts && status.conflicts.length
                ? 'Конфликт ограничений — правка не применена'
                : 'Решатель не сошёлся';
            visible = true;
        } else if (status.dof === 0) {
            color = '#2aa84a';
            text = 'Эскиз полностью определён';
            visible = true;
        } else if (typeof status.dof === 'number' && status.dof > 0) {
            color = '#1a72d0';
            text = `Недоопределён · степеней свободы: ${status.dof}`;
            visible = true;
        }
        // dof неизвестен — узел остаётся, но невидим (см. visibility ниже).
    }

    return (
        <div style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '5px 12px', borderRadius: 6, pointerEvents: 'none',
            background: 'rgba(30,32,38,0.92)', color: '#fff',
            font: '13px/1.2 system-ui, sans-serif', whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            // Узел присутствует всегда — прячем стилем, а не размонтированием.
            visibility: visible ? 'visible' : 'hidden',
        }}>
            <span style={{
                width: 9, height: 9, borderRadius: '50%',
                background: color, flex: '0 0 auto',
            }}/>
            {text}
        </div>
    );
}

/*
 * Контейнер для ПОРТАЛА оверлея.
 *
 * ЗАЧЕМ ПОРТАЛ. Оверлей эскиза — сосед 3D-канваса в общем контейнере. Его
 * содержимое непрерывно меняется во время рисования (появляются размерные
 * линии, поля ввода, подсказки), и React пересчитывает позиции вставки узлов
 * рядом с ЖИВЫМ WebGL-канвасом. На этом он раз за разом падал с
 * «insertBefore: узел не является дочерним», роняя весь редактор.
 *
 * Точечные фиксы (общий svg, стабильные ключи, обёртки, разделение на слои)
 * лечили по одному симптому за раз. Портал убирает саму возможность: оверлей
 * рендерится в СОБСТВЕННЫЙ div, созданный вручную и подложенный в область
 * просмотра. Для React это отдельное поддерево — что бы в нём ни менялось,
 * узлов вокруг канваса он не трогает.
 */
function useOverlayPortal() {
    const [host, setHost] = useState(null);

    useEffect(() => {
        /*
         * 🔴 КОРНЕВОЙ ФИКС insertBefore (2026-08-19).
         *
         * Раньше host-узел клали через appendChild ПРЯМО в viewportPane —
         * а это React-управляемый контейнер (в нём canvasLayer + overlayLayer
         * как JSX-дети). Ручной ребёнок в дереве, которым владеет React:
         * при любом обновлении overlayLayer (смена оверлеев по режиму)
         * реконсилятор ждал определённый набор детей viewportPane, а там
         * висел чужой узел → «insertBefore: узел не является дочерним» и
         * следом потеря WebGL-контекста. Все прошлые фиксы (порталы, слои,
         * ключи) лечили симптомы, но host всё равно сидел внутри React-узла.
         *
         * Теперь host живёт в document.body (position:fixed) — физически ВНЕ
         * всего React-дерева, React его не видит и не реконсилит. Геометрию
         * синхронизируем с viewportPane: оверлей визуально ложится ровно
         * поверх области просмотра, screen-координаты project() совпадают.
         */
        const anchor = document.querySelector('[class*="viewportPane"]');
        if (!anchor) return;

        const el = document.createElement('div');
        el.setAttribute('data-cad-overlay-host', '');
        el.style.position = 'fixed';
        el.style.pointerEvents = 'none';   // клики проходят к канвасу
        el.style.zIndex = '5';

        const sync = () => {
            const r = anchor.getBoundingClientRect();
            el.style.left = r.left + 'px';
            el.style.top = r.top + 'px';
            el.style.width = r.width + 'px';
            el.style.height = r.height + 'px';
        };
        sync();
        document.body.appendChild(el);
        setHost(el);

        // держим геометрию host в соответствии с viewportPane
        const ro = new ResizeObserver(sync);
        ro.observe(anchor);
        window.addEventListener('resize', sync);
        window.addEventListener('scroll', sync, true);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', sync);
            window.removeEventListener('scroll', sync, true);
            try { document.body.removeChild(el); } catch (e) { /* уже удалён */ }
        };
    }, []);

    return host;
}


const SketchOverlayUI = () => {
    // Собственный DOM-узел вне React-дерева канваса (см. useOverlayPortal).
    const portalHost = useOverlayPortal();
    const mode = useCadStore((s) => s.mode);
    const exitSketchMode = useCadStore((s) => s.exitSketchMode);
    const cancelSelectPlane = useCadStore((s) => s.cancelSelectPlane);
    const activeTool = useCadStore((s) => s.activeSketchTool);
    const activeSketchId = useCadStore((s) => s.activeSketchId);
    const sketches = useCadStore((s) => s.doc.sketches);
    const activeSketch = sketches.find((s) => s.id === activeSketchId);
    const pendingShape = useCadStore((s) => s.pendingShape);
    const solveStatus = useCadStore((s) => s.sketchSolveStatus);

    /*
     * Позицию оверлея замеряем ПОСЛЕ каждого коммита — здесь читать layout
     * безопасно (в отличие от рендера, см. DrawDimensions). Плюс на ресайз:
     * при смене размера окна оверлей сдвигается.
     */
    useEffect(() => {
        refreshOverlayOffset();
        window.addEventListener('resize', refreshOverlayOffset);
        return () => window.removeEventListener('resize', refreshOverlayOffset);
    });

    const [, setSolverReady] = useState(false);
    useEffect(() => {
        const solver = getSolverClient();
        if (solver.ready) { setSolverReady(true); return; }
        solver.init().then(() => setSolverReady(true))
            .catch((e) => console.error('[SketchOverlayUI] solver init failed:', e));
    }, []);

    // По Enter где-то ещё — финализируем pending; по Escape — отменяем
    useEffect(() => {
        if (mode !== 'sketch') return;
        const onKey = (e) => {
            if (!pendingShape) return;
            if (e.key === 'Enter') {
                e.preventDefault();
                if (typeof window.__kubikonCommitDraw === 'function') {
                    window.__kubikonCommitDraw();
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                if (typeof window.__kubikonCancelDraw === 'function') {
                    window.__kubikonCancelDraw();
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mode, pendingShape]);

    // При «Завершить эскиз» если есть pending — фиксируем его сначала
    const onFinish = () => {
        if (pendingShape && typeof window.__kubikonCommitDraw === 'function') {
            window.__kubikonCommitDraw();
        }
        exitSketchMode();
    };

    /*
     * ВАЖНО: корень оверлея — ОДИН на оба режима.
     *
     * Раньше select_plane и sketch возвращали ДВА разных <div> с одинаковым
     * id="kubikon-cad-overlay-root". При переходе «выбрал плоскость → нажал
     * инструмент» React менял ветку дерева, и в этот момент в DOM оказывались
     * два узла с одним id: getElementById (строка ~198) мог вернуть уже
     * отсоединённый, а React — не найти тот, что собирался удалить. Отсюда
     * «Failed to execute 'removeChild'» и следом потеря WebGL-контекста.
     *
     * Теперь корень стабилен, меняется только его содержимое: React обновляет
     * детей, а не пересоздаёт контейнер.
     */
    // Портал ещё не готов (первый кадр) — ничего не рисуем.
    if (!portalHost) return null;

    if (mode === 'select_plane') {
        return createPortal(
            <div id="kubikon-cad-overlay-root" className={cl.overlayRoot} key="cad-overlay" data-transparent>
                <div className={cl.banner}>Выбери плоскость для нового эскиза</div>
                <button className={cl.cancelBtn} onClick={cancelSelectPlane}>Отмена</button>
            </div>,
            portalHost,
        );
    }

    if (mode === 'sketch') {
        return createPortal(
            <div id="kubikon-cad-overlay-root" className={cl.overlayRoot} key="cad-overlay" data-transparent>
                {/* Боковая палитра и кнопка «Завершить» убраны — все инструменты
                    теперь в верхнем ribbon (TopBar SKETCH_RIBBON), включая
                    зелёную кнопку «Завершить эскиз» справа. */}
                {/*
                  * ⚠️ У КАЖДОГО соседа — стабильный key, и это не косметика.
                  *
                  * Здесь рядом живут четыре узла, которые появляются и исчезают
                  * независимо: DrawDimensions, две панели по активному
                  * инструменту и бейдж солвера (он возвращает null, пока dof
                  * неизвестен). При выборе инструмента менялись сразу
                  * несколько, React пересчитывал позиции вставки в одном
                  * контейнере — и падал с «insertBefore: узел не является
                  * дочерним». Именно на этом умирал редактор при клике по
                  * «прямоугольнику»: ошибка рендера роняла дерево целиком,
                  * а следом терялся WebGL-контекст.
                  *
                  * С ключами React сопоставляет детей по идентичности, а не по
                  * порядку, и перестановок соседей не происходит.
                  */}
                {/* Каждый «мигающий» блок сидит в СВОЁМ постоянном контейнере.
                    Так количество и порядок детей оверлея не меняются никогда:
                    внутри контейнера может появиться или исчезнуть что угодно,
                    но позиции соседей остаются на месте — а именно на их
                    пересчёте React и падал с insertBefore. */}
                {/* display:contents — обёртка есть в дереве React, но НЕ создаёт
                    собственного бокса: вёрстка и клики ведут себя ровно как
                    раньше (панели позиционируются absolute относительно
                    оверлея, канвас под ними остаётся кликабельным). */}
                {/* Блоки можно гасить по одному через localStorage.cadOff —
                    см. isOff() выше. Нужно, чтобы найти виновника падения
                    экспериментом, а не перебором гипотез. */}
                <div key="dims" style={WRAP}>{!isOff('dims') && <DrawDimensions/>}</div>
                <div key="polygon-sides" style={WRAP}>{!isOff('panels') && activeTool === 'polygon' && <PolygonSidesPanel/>}</div>
                <div key="offset-dist" style={WRAP}>{!isOff('panels') && activeTool === 'offsetSketch' && <OffsetDistancePanel/>}</div>
                <div key="text-tool" style={WRAP}>{activeTool === 'sketchText' && <TextToolPanel/>}</div>
                <div key="solve-badge" style={WRAP}>{!isOff('badge') && <SketchStatusBadge status={solveStatus}/>}</div>
                <div className={cl.hint}>
                    {!pendingShape && activeTool === null && (activeSketch?.primitives?.length > 0
                        ? 'Тащи точки чтобы менять размер · '
                        : 'Выбери инструмент сверху · ')}
                    {!pendingShape && activeTool === 'line'      && 'Зажми ЛКМ и тяни ИЛИ кликай две точки · '}
                    {!pendingShape && activeTool === 'rectangle' && 'Зажми ЛКМ и тяни ИЛИ кликай два угла · '}
                    {!pendingShape && activeTool === 'circle'    && 'Зажми ЛКМ в центре и тяни ИЛИ кликай центр и точку · '}
                    {!pendingShape && activeTool === 'arc'       && 'Кликай: центр → начало → конец дуги · '}
                    {!pendingShape && activeTool === 'polygon'   && 'Задай число сторон сверху, кликни центр и вершину · '}
                    {!pendingShape && activeTool === 'dimension' && 'Кликни линию (длина) или окружность (диаметр), введи размер · '}
                    {!pendingShape && activeTool === 'trim'      && 'Кликай по линиям/дугам/окружностям чтобы удалить их · '}
                    {!pendingShape && activeTool === 'offsetSketch' && 'Задай смещение сверху, кликни ВНУТРЬ замкнутого контура · '}
                    {pendingShape && pendingShape.phase === 'click' && 'Кликни вторую точку чтобы зафиксировать · '}
                    {pendingShape && pendingShape.phase === 'placed' && 'Введи размеры или нажми ✓ для применения · Esc — отменить · '}
                    {pendingShape && pendingShape.phase === 'drag' && 'Тяни мышь до нужного размера · '}
                    Колесо — масштаб
                </div>
            </div>,
            portalHost,
        );
    }

    return null;
};

export default SketchOverlayUI;
